/**
 * After redeem metafield change: recalculate discounts + refresh cart UI.
 * Horizon: Shopify.actions + shopify:cart:* (sectionRenderer).
 * Dawn: renderContents / #CartDrawer only (never blank outer section).
 * @see https://shopify.dev/docs/storefronts/themes/best-practices/standard-actions
 * @see https://shopify.dev/docs/api/ajax/reference/cart
 */
(function (global) {
  function routesRoot() {
    if (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) {
      return global.Shopify.routes.root;
    }
    return "/";
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function sectionInner(html, selector) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  function softFill(targetSelector, html, sourceSelector) {
    const target = document.querySelector(targetSelector);
    if (!target || !html) return false;
    const inner = sectionInner(html, sourceSelector || targetSelector);
    if (inner === null) return false;
    target.innerHTML = inner;
    return true;
  }

  function findDawnDrawer() {
    return document.querySelector("cart-drawer");
  }

  function isHorizonCart() {
    return Boolean(document.querySelector("cart-items-component, cart-drawer-component"));
  }

  function collectDawnSectionIds(dawnDrawer) {
    const ids = [];
    const push = (id) => {
      if (id && !ids.includes(id)) ids.push(id);
    };

    if (dawnDrawer && typeof dawnDrawer.getSectionsToRender === "function") {
      try {
        dawnDrawer.getSectionsToRender().forEach((s) => push(s.id));
      } catch (_error) {
        /* ignore */
      }
    }

    ["cart-drawer", "cart-icon-bubble", "cart-notification", "main-cart-items", "main-cart-footer"].forEach(
      (id) => {
        if (
          document.getElementById("shopify-section-" + id) ||
          document.getElementById(id) ||
          (id === "cart-drawer" && document.querySelector("cart-drawer, #CartDrawer"))
        ) {
          push(id);
        }
      },
    );

    return ids.slice(0, 5);
  }

  async function forceAjaxRecalc(sectionIds) {
    const body = {
      attributes: { _points_r: String(Date.now()) },
    };
    if (sectionIds && sectionIds.length > 0) {
      body.sections = sectionIds;
      body.sections_url = global.location.pathname || "/";
    }

    const response = await fetch(routesRoot() + "cart/update.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error("cart/update.js failed");
    return response.json();
  }

  function normalizeActionLines(cart) {
    if (!cart) return [];
    const raw = cart.lines;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.nodes)) return raw.nodes;
    if (Array.isArray(raw.edges)) {
      return raw.edges.map((edge) => edge && edge.node).filter(Boolean);
    }
    return [];
  }

  /** Official path for Horizon (and any theme with standard actions). */
  async function refreshWithShopifyActions() {
    const actions = global.Shopify && global.Shopify.actions;
    if (!actions || typeof actions.updateCart !== "function") return false;

    try {
      let cart = null;
      if (typeof actions.getCart === "function") {
        const result = await actions.getCart();
        cart = result && result.cart;
      }

      const lines = normalizeActionLines(cart);
      if (lines.length > 0) {
        const line = lines[0];
        if (!line || !line.id) return false;
        await actions.updateCart({
          lines: [{ id: line.id, quantity: line.quantity }],
        });
        return true;
      }

      // Empty cart: note bump triggers shopify:cart:note-update + theme refresh
      const note = (cart && cart.note) || "";
      await actions.updateCart({ note: note });
      return true;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Horizon cart-items-component: external shopify:cart:* without sections
   * → sectionRenderer.renderSection (official theme path).
   */
  function dispatchHorizonStandardRefresh(cartAjax) {
    const itemCount =
      cartAjax && typeof cartAjax.item_count === "number" ? cartAjax.item_count : 0;

    ["shopify:cart:lines-update", "shopify:cart:note-update"].forEach((name) => {
      let resolveFn = function () {};
      const promise = new Promise(function (resolve) {
        resolveFn = resolve;
      });

      const event = new Event(name, { bubbles: true });
      Object.defineProperty(event, "promise", { value: promise, enumerable: true });
      Object.defineProperty(event, "action", { value: "update", enumerable: true });

      document.dispatchEvent(event);

      resolveFn({
        cart: { totalQuantity: itemCount },
        detail: {
          itemCount: itemCount,
          source: "points-redeem",
          didError: false,
        },
      });
    });

    // Older Horizon forks
    document.dispatchEvent(
      new CustomEvent("cart:update", {
        bubbles: true,
        detail: {
          data: { itemCount: itemCount, source: "points-redeem" },
        },
      }),
    );
  }

  function applyDawnSections(sections, dawnDrawer, wasOpen) {
    if (!sections) return false;

    if (dawnDrawer && typeof dawnDrawer.renderContents === "function") {
      try {
        dawnDrawer.renderContents({ sections: sections });
        if (!wasOpen && typeof dawnDrawer.close === "function") {
          try {
            dawnDrawer.close();
          } catch (_error) {
            /* ignore */
          }
        }
        return true;
      } catch (_error) {
        /* soft fill */
      }
    }

    let updated = false;
    if (sections["cart-drawer"]) {
      updated = softFill("#CartDrawer", sections["cart-drawer"], "#CartDrawer") || updated;
    }
    Object.keys(sections).forEach((id) => {
      if (/cart-icon|icon-bubble/i.test(id)) {
        const bubble =
          document.getElementById("shopify-section-" + id) || document.getElementById(id);
        if (!bubble) return;
        const inner =
          sectionInner(sections[id], "#shopify-section-" + id) ||
          sectionInner(sections[id], ".shopify-section");
        if (inner !== null) {
          bubble.innerHTML = inner;
          updated = true;
        }
      }
    });
    return updated;
  }

  function isOpen(drawer) {
    if (!drawer) return false;
    return (
      drawer.classList.contains("active") ||
      drawer.hasAttribute("open") ||
      drawer.getAttribute("aria-hidden") === "false"
    );
  }

  async function afterRedeemChange() {
    const onCartPage = /\/cart\/?$/.test(global.location.pathname);
    const dawnDrawer = findDawnDrawer();
    const wasOpen = isOpen(dawnDrawer);

    try {
      await sleep(300);

      // 1) Prefer standard actions (Horizon / Spring '26 themes)
      const viaActions = await refreshWithShopifyActions();

      // 2) Always recalc Ajax cart discounts (Discount Function source of truth)
      const dawnIds = collectDawnSectionIds(dawnDrawer);
      const cartAjax = await forceAjaxRecalc(viaActions ? [] : dawnIds);

      // 3) Horizon: tell cart-items-component to re-fetch its section
      if (isHorizonCart()) {
        dispatchHorizonStandardRefresh(cartAjax);
      }

      // 4) Dawn: themed drawer HTML
      applyDawnSections(cartAjax.sections, dawnDrawer, wasOpen);

      if (cartAjax && cartAjax.sections && cartAjax.sections["main-cart-footer"]) {
        softFill("#main-cart-footer", cartAjax.sections["main-cart-footer"], "#main-cart-footer");
      }

      if (onCartPage && !dawnDrawer && !isHorizonCart()) {
        global.location.reload();
      }
    } catch (_error) {
      if (isHorizonCart()) dispatchHorizonStandardRefresh(null);
      if (onCartPage) global.location.reload();
    }
  }

  global.PointsCartRefresh = {
    afterRedeemChange: afterRedeemChange,
  };
})(window);
